import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { motoBrandService, motoModelService } from "@/api/services"
import type { MotoBrandResponseDto } from "@/api/types/motoBrand.types"
import type { MotoModelResponseDto, MotoModelListParams } from "@/api/types/motoModel.types"
import Sidebar from "@/components/Sidebar"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Loader2, ArrowLeft, Edit2, Trash2, Plus, Save } from "lucide-react"

export default function MotoBrandDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [brand, setBrand] = useState<MotoBrandResponseDto | null>(null)
  const [models, setModels] = useState<MotoModelResponseDto[]>([])
  const [loading, setLoading] = useState(true)
  const [modelsLoading, setModelsLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  // Brand edit states
  const [isEditingBrand, setIsEditingBrand] = useState(false)
  const [brandFormData, setBrandFormData] = useState({ name: "" })
  const [savingBrand, setSavingBrand] = useState(false)
  const [togglingBrandActive, setTogglingBrandActive] = useState(false)
  const [deletingBrand, setDeletingBrand] = useState(false)
  const [deleteBrandDialogOpen, setDeleteBrandDialogOpen] = useState(false)

  // Models states
  const [params, setParams] = useState<MotoModelListParams>({
    page: 1,
    limit: 50,
    brandId: id,
  })
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Model dialog states
  const [createModelDialogOpen, setCreateModelDialogOpen] = useState(false)
  const [editModelDialogOpen, setEditModelDialogOpen] = useState(false)
  const [deleteModelDialogOpen, setDeleteModelDialogOpen] = useState(false)
  const [selectedModel, setSelectedModel] = useState<MotoModelResponseDto | null>(null)

  // Model form states
  const [modelFormData, setModelFormData] = useState({ name: "", brandId: id || "" })
  const [savingModel, setSavingModel] = useState(false)
  const [deletingModel, setDeletingModel] = useState(false)

  useEffect(() => {
    if (id) {
      loadBrand()
      setParams((prev) => ({ ...prev, brandId: id }))
    }
  }, [id])

  useEffect(() => {
    if (id) {
      loadModels()
    }
  }, [params, id])

  const loadBrand = async () => {
    if (!id) return
    setLoading(true)
    setError("")
    try {
      const brandData = await motoBrandService.getMotoBrandById(id)
      setBrand(brandData)
      setBrandFormData({ name: brandData.name })
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to load brand"
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const loadModels = async () => {
    if (!id) return
    setModelsLoading(true)
    setError("")
    try {
      const response = await motoModelService.getMotoModels(params)
      setModels(response.data)
      setTotal(response.total)
      // Sync currentPage with response, but only if it matches what we requested
      const responsePage = response.page || params.page || 1
      setCurrentPage(responsePage)
      setTotalPages(response.totalPages || Math.ceil(response.total / (params.limit || 50)))
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to load models"
      setError(errorMessage)
      // On error, reset to page 1 if we're on an invalid page
      if (params.page !== undefined && params.page > totalPages && totalPages > 0) {
        setCurrentPage(1)
        setParams((prevParams) => ({ ...prevParams, page: 1 }))
      }
    } finally {
      setModelsLoading(false)
    }
  }

  const handleEditBrand = () => {
    setIsEditingBrand(true)
  }

  const handleCancelEditBrand = () => {
    setIsEditingBrand(false)
    if (brand) {
      setBrandFormData({ name: brand.name })
    }
  }

  const handleSaveBrand = async () => {
    if (!id || !brandFormData.name.trim()) {
      setError("Brand name is required")
      return
    }

    setSavingBrand(true)
    setError("")
    setSuccess("")

    try {
      const updatedBrand = await motoBrandService.updateMotoBrand(id, {
        name: brandFormData.name.trim(),
      })
      setBrand(updatedBrand)
      setSuccess("Brand updated successfully")
      setIsEditingBrand(false)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to update brand"
      setError(errorMessage)
    } finally {
      setSavingBrand(false)
    }
  }

  const handleToggleBrandActive = async (nextIsActive: boolean) => {
    if (!id || !brand) return

    const prevIsActive = brand.isActive
    setTogglingBrandActive(true)
    setError("")
    setSuccess("")

    // Optimistic UI update
    setBrand({ ...brand, isActive: nextIsActive })

    try {
      const updatedBrand = await motoBrandService.updateMotoBrand(id, {
        isActive: nextIsActive,
      })
      setBrand(updatedBrand)
      setSuccess(`Brand ${nextIsActive ? "activated" : "deactivated"} successfully`)
    } catch (err: any) {
      // Rollback on error
      setBrand({ ...brand, isActive: prevIsActive })
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to update brand status"
      setError(errorMessage)
    } finally {
      setTogglingBrandActive(false)
    }
  }

  const handleDeleteBrand = async () => {
    if (!id) return

    setDeletingBrand(true)
    setError("")
    setDeleteBrandDialogOpen(false)

    try {
      await motoBrandService.deleteMotoBrand(id)
      navigate("/moto-brands")
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to delete brand"
      setError(errorMessage)
      setDeletingBrand(false)
    }
  }

  const handleCreateModel = () => {
    setModelFormData({ name: "", brandId: id || "" })
    setSelectedModel(null)
    setCreateModelDialogOpen(true)
  }

  const handleEditModel = (model: MotoModelResponseDto) => {
    setModelFormData({ name: model.name, brandId: model.brandId })
    setSelectedModel(model)
    setEditModelDialogOpen(true)
  }

  const handleDeleteModel = (model: MotoModelResponseDto) => {
    setSelectedModel(model)
    setDeleteModelDialogOpen(true)
  }

  const handleSubmitCreateModel = async () => {
    if (!modelFormData.name.trim() || !modelFormData.brandId) {
      setError("Model name is required")
      return
    }

    setSavingModel(true)
    setError("")
    setSuccess("")

    try {
      await motoModelService.createMotoModel({
        name: modelFormData.name.trim(),
        brandId: modelFormData.brandId,
      })
      setSuccess("Model created successfully")
      setCreateModelDialogOpen(false)
      setModelFormData({ name: "", brandId: id || "" })
      loadModels()
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to create model"
      setError(errorMessage)
    } finally {
      setSavingModel(false)
    }
  }

  const handleSubmitEditModel = async () => {
    if (!selectedModel || !modelFormData.name.trim() || !modelFormData.brandId) {
      setError("Model name is required")
      return
    }

    setSavingModel(true)
    setError("")
    setSuccess("")

    try {
      await motoModelService.updateMotoModel(selectedModel.id, {
        name: modelFormData.name.trim(),
        brandId: modelFormData.brandId,
      })
      setSuccess("Model updated successfully")
      setEditModelDialogOpen(false)
      setModelFormData({ name: "", brandId: id || "" })
      setSelectedModel(null)
      loadModels()
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to update model"
      setError(errorMessage)
    } finally {
      setSavingModel(false)
    }
  }

  const handleConfirmDeleteModel = async () => {
    if (!selectedModel) return

    setDeletingModel(true)
    setError("")
    setDeleteModelDialogOpen(false)

    try {
      await motoModelService.deleteMotoModel(selectedModel.id)
      setSuccess("Model deleted successfully")
      setSelectedModel(null)
      loadModels()
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || "Failed to delete model"
      setError(errorMessage)
      setDeletingModel(false)
    }
  }

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return
    setCurrentPage(newPage)
    setParams((prevParams) => ({ ...prevParams, page: newPage }))
  }

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8 bg-background min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    )
  }

  if (!brand) {
    return (
      <div className="flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
          <div className="max-w-2xl">
            <Card className="p-6">
              <div className="text-center">
                <p className="text-destructive mb-4">{error || "Brand not found"}</p>
                <Button onClick={() => navigate("/moto-brands")} variant="outline">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Brands
                </Button>
              </div>
            </Card>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex">
      <Sidebar />

      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-7xl">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" size="sm" onClick={() => navigate("/moto-brands")}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Brands
            </Button>
            <h1 className="text-3xl font-bold text-foreground">Brand Details</h1>
          </div>

          {/* Success message */}
          {success && (
            <div className="bg-success/10 border border-success/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-success">{success}</p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-6">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Brand Information */}
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Brand Information</h2>
              {!isEditingBrand && (
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleEditBrand}>
                      <Edit2 className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeleteBrandDialogOpen(true)}
                      disabled={deletingBrand}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={brand.isActive}
                      onCheckedChange={handleToggleBrandActive}
                      disabled={togglingBrandActive || deletingBrand}
                    />
                    <span
                      className={`text-sm font-medium ${
                        togglingBrandActive ? "opacity-70" : ""
                      }`}
                    >
                      Active
                    </span>
                    {togglingBrandActive && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                  </div>
                </div>
              )}
            </div>

            {isEditingBrand ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="brand-name">Brand Name</Label>
                  <Input
                    id="brand-name"
                    value={brandFormData.name}
                    onChange={(e) => setBrandFormData({ name: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && handleSaveBrand()}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={handleSaveBrand} disabled={savingBrand}>
                    {savingBrand ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save
                      </>
                    )}
                  </Button>
                  <Button variant="outline" onClick={handleCancelEditBrand} disabled={savingBrand}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Name</Label>
                  <p className="text-foreground font-medium">{brand.name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Created At</Label>
                  <p className="text-foreground font-medium">
                    {new Date(brand.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Updated At</Label>
                  <p className="text-foreground font-medium">
                    {new Date(brand.updatedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}
          </Card>

          {/* Models Section */}
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">Models</h2>
                <Button onClick={handleCreateModel}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Model
                </Button>
              </div>

              {modelsLoading ? (
                <div className="p-8 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : models.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <p>No models found for this brand</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Brand</TableHead>
                          <TableHead>Created At</TableHead>
                          <TableHead>Updated At</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {models.map((model) => (
                          <TableRow key={model.id}>
                            <TableCell className="font-medium">{model.name}</TableCell>
                            <TableCell>{model.brandName}</TableCell>
                            <TableCell>
                              {new Date(model.createdAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              {new Date(model.updatedAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditModel(model)}
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteModel(model)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="p-4 border-t border-border flex items-center justify-between">
                      <div className="text-sm text-muted-foreground">
                        Showing {((currentPage - 1) * (params.limit || 50)) + 1} to{" "}
                        {Math.min(currentPage * (params.limit || 50), total)} of {total} models
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          Page {currentPage} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>
        </div>
      </main>

      {/* Delete Brand Dialog */}
      <Dialog open={deleteBrandDialogOpen} onOpenChange={setDeleteBrandDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Moto Brand</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{brand.name}"? This will also delete all models
              associated with this brand. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteBrandDialogOpen(false)}
              disabled={deletingBrand}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteBrand} disabled={deletingBrand}>
              {deletingBrand ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Model Dialog */}
      <Dialog open={createModelDialogOpen} onOpenChange={setCreateModelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Moto Model</DialogTitle>
            <DialogDescription>Add a new motorcycle model to this brand</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-model-name">Model Name</Label>
              <Input
                id="create-model-name"
                placeholder="e.g., CBR1000RR"
                value={modelFormData.name}
                onChange={(e) => setModelFormData({ ...modelFormData, name: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handleSubmitCreateModel()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateModelDialogOpen(false)}
              disabled={savingModel}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmitCreateModel} disabled={savingModel}>
              {savingModel ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Model Dialog */}
      <Dialog open={editModelDialogOpen} onOpenChange={setEditModelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Moto Model</DialogTitle>
            <DialogDescription>Update the motorcycle model information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-model-name">Model Name</Label>
              <Input
                id="edit-model-name"
                placeholder="e.g., CBR1000RR"
                value={modelFormData.name}
                onChange={(e) => setModelFormData({ ...modelFormData, name: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && handleSubmitEditModel()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditModelDialogOpen(false)}
              disabled={savingModel}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmitEditModel} disabled={savingModel}>
              {savingModel ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Model Dialog */}
      <Dialog open={deleteModelDialogOpen} onOpenChange={setDeleteModelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Moto Model</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedModel?.name}"? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteModelDialogOpen(false)}
              disabled={deletingModel}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDeleteModel} disabled={deletingModel}>
              {deletingModel ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

